import Paper from "@material-ui/core/Paper";
import ChipArrayField from "components/fields/ChipArrayField";
import { DateFormatField } from "components/fields/DateFormatField";
import { DateTimeField } from "components/fields/DateFormatField";
import { SelectEnumField } from "components/fields/SelectEnumField";
import { YesNoField } from "components/fields/YesNoField";
import { FlexLayout, FlexRow } from "components/FlexShowLayout";
import { MyDatagrid } from "components/HistoryGrid/Grid";
import { createCellStyle } from "components/HistoryGrid/utils";
import { AppReferenceManyField } from "components/list/AppReferenceManyField";
import { ScrollingWrapperInCard } from "components/ScrollingWrapper";
import { DeviationMainCategory } from "entities/DeviationMainCategory";
import { DeviationStatus } from "entities/DeviationStatus";
import { DeviationType } from "entities/DeviationType";
import React from "react";
import {
	ChipField,
	FunctionField,
	ReferenceArrayField,
	ReferenceField,
	SimpleShowLayout,
	SingleFieldList,
	Tab,
	TextField,
} from "react-admin";

import ChatContainer from "../Chat";

const UserField = (props) => {
	return (<ReferenceField {...props} reference="users" link={false} >
		<FunctionField
			render={(record) =>
				record ? `${record.first_name || ""} ${record.last_name || ""}` : ""
			}
		/>
	</ReferenceField>);
}
UserField.defaultProps = {
    addLabel: true,
};

const DeviationGeneralGrid = () => {
	return (
		<SimpleShowLayout>			
			<FlexLayout flatRow>
				<FlexRow>
					<TextField source="id" />					
					<SelectEnumField
						source="deviation_main_category"
						enumName="deviation_main_category"
						enumObj={DeviationMainCategory}
					/>
					<SelectEnumField source="deviation_type" enumName="deviation_type" enumObj={DeviationType} />
					<SelectEnumField source="status" enumName="deviation_status" enumObj={DeviationStatus} />
				</FlexRow>
				<FlexRow>
					<DateFormatField source="created_at" />
					<UserField source="created_by" />
					<UserField source="duty_manager" />
					{/* TODO: needs to be fixed */}
					{/* <UserField source="managers_to_notify" /> */}
					<UserField source="assignee_1" />
					<UserField source="assignee_2" />					
				</FlexRow>
				<FlexRow>
					<ReferenceField
						source="order_id"
						reference="orders"
						link={(record, reference) => `/orders/${record.order_id}/show`}
					>
						<TextField source="id" />
					</ReferenceField>
					<ReferenceField source="terminal_id" reference="terminal" link={false}>
						<TextField source="name" />
					</ReferenceField>
					<ReferenceField source="company_id" reference="customer-companies" link={false}>
						<TextField source="full_name" />
					</ReferenceField>
				</FlexRow>
				<FlexRow>					
					<TextField source="kilos" />
					<TextField source="price_per_kilo" />
					<TextField source="pallets" />
					<TextField source="boxes" />
				</FlexRow>				
				<FlexRow>
					<YesNoField source="is_storage_correction_required" />
					<ChipArrayField source="area_names" label="resources.deviations.fields.area_ids" />
					<YesNoField source="is_articles_required" />
					<FunctionField
						source="articles"
						render={(record) =>
							record ? <span>{record.articles?.map((item) => item.article_code).join(", ")}</span> : ""
						}
					/>
				</FlexRow>
				<FlexRow>
					<TextField source="description" />
					<TextField source="customer_notes" />
				</FlexRow>
				<FlexRow>
					<YesNoField source="is_claim" />
					<TextField source="claim_value" />
					<TextField source="claim_invoice" />
				</FlexRow>
			</FlexLayout>
		</SimpleShowLayout>
	);
};

export const DeviationHistoryGrid = (props) => {
	const nextForRecordId = React.useMemo(() => {
		return props.ids
			? props.ids.reduce((res, curr, index, arr) => {
					return {
						...res,
						[curr]: arr[index + 1] ? arr[index + 1] : null,
					};
			  }, {})
			: {};
	}, [props.ids]);

	const cellStyle = createCellStyle(props.data, nextForRecordId, ["updated_at"]);

	return (
		<Paper variant="outlined">
			<ScrollingWrapperInCard>
				<MyDatagrid {...props} cellStyle={cellStyle}>
					<ReferenceField
						sortable={false}
						source="terminal_id"
						reference="terminal"
						link={false}
						label="resources.deviations.fields.terminal_id"
					>
						<TextField sortable={false} source="name" />
					</ReferenceField>
					<ReferenceField
						sortable={false}
						source="company_id"
						reference="customer-companies"
						link={false}
						label="resources.deviations.fields.company_id"
					>
						<TextField sortable={false} source="short_name" />
					</ReferenceField>
					<TextField
						sortable={false}
						source="updated_by_name"
						label="resources.deviations.fields.updated_by"
					/>
					<DateTimeField
						sortable={false}
						source="updated_at"
						label="resources.deviations.fields.updated_by"
					/>
					<TextField
						sortable={false}
						source="order_id"
						label="resources.deviations.fields.order_id"
					/>
					<TextField
						sortable={false}
						source="description"
						label="resources.deviations.fields.description"
					/>

					<SelectEnumField
						source="deviation_main_category"
						enumName="deviation_main_category"
						enumObj={DeviationMainCategory}
						sortable={false}
						label="resources.deviations.fields.deviation_main_category"
					/>
					<SelectEnumField
						source="deviation_type"
						enumName="deviation_type"
						enumObj={DeviationType}
						sortable={false}
						label="resources.deviations.fields.deviation_type"
					/>
					<SelectEnumField
						source="status"
						enumName="deviation_status"
						enumObj={DeviationStatus}
						sortable={false}
						label="resources.deviations.fields.status"
					/>

					<ReferenceField
						source="duty_manager"
						reference="users"
						link={false}
						sortable={false}
						label="resources.deviations.fields.duty_manager"
					>
						<FunctionField
							render={(record) =>
								record ? `${record.first_name || ""} ${record.last_name || ""}` : ""
							}
						/>
					</ReferenceField>
					<ReferenceArrayField
						source="managers_to_notify"
						reference="users"
						sortable={false}
						label="resources.deviations.fields.managers_to_notify"
					>
						<SingleFieldList linkType={false}>
							<ChipField source="name" size="small" />
						</SingleFieldList>
					</ReferenceArrayField>

					<ChipArrayField
						source="area_names"
						sortable={false}
						label="resources.deviations.fields.area_ids"
					/>
					<FunctionField
						source="articles"
						sortable={false}
						render={(record) =>
							record ? (
								<span>{record.articles?.map((item) => item.article_code).join(", ")}</span>
							) : (
								""
							)
						}
					/>

					<YesNoField
						sortable={false}
						source="is_articles_required"
						label="resources.deviations.fields.is_articles_required"
					/>
					<YesNoField
						sortable={false}
						source="is_storage_correction_required"
						label="resources.deviations.fields.is_storage_correction_required"
					/>

					<TextField source="kilos" sortable={false} label="resources.deviations.fields.kilos" />
					<TextField source="price_per_kilo" label="resources.deviations.fields.price_per_kilo" />
					<TextField source="pallets" label="resources.deviations.fields.pallets" />
					<TextField source="boxes" label="resources.deviations.fields.boxes" />
				</MyDatagrid>
			</ScrollingWrapperInCard>
		</Paper>
	);
};

export const GeneralTab = (props) => {
	return (
		<Tab {...props}>
			<DeviationGeneralGrid />
		</Tab>
	);
};

export const HistoryTab = (props) => {
	return (
		<Tab label="" {...props}>
			<AppReferenceManyField
				reference="deviations-history"
				target="original_id"
				{...props}
			>
				<DeviationHistoryGrid />
			</AppReferenceManyField>
		</Tab>
	);
};

export const ChatTab = (props) => {
	return (
		<Tab {...props}>
			<ChatContainer />
		</Tab>
	);
};
